import { useState, useMemo } from "react";

interface PendingRestaurant {
  id: string;
  restaurantName: string;
  city: string;
  cuisine: string;
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
}

const MONTH_NAMES = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const StatsChart = ({ applications }: { applications: PendingRestaurant[] }) => {
  const [activeTab, setActiveTab] = useState<"monthly" | "approval">("monthly");

  const monthlyData = useMemo(() => {
    const now = new Date();
    const months: { label: string; total: number; approved: number; rejected: number; pending: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = MONTH_NAMES[d.getMonth()];
      const year = d.getFullYear();
      const month = d.getMonth();
      const inMonth = applications.filter((a) => {
        const ad = new Date(a.appliedAt);
        return ad.getFullYear() === year && ad.getMonth() === month;
      });
      months.push({
        label,
        total: inMonth.length,
        approved: inMonth.filter((a) => a.status === "approved").length,
        rejected: inMonth.filter((a) => a.status === "rejected").length,
        pending: inMonth.filter((a) => a.status === "pending").length,
      });
    }
    return months;
  }, [applications]);

  const maxTotal = Math.max(...monthlyData.map((m) => m.total), 1);

  const totalApps = applications.length;
  const approvedCount = applications.filter((a) => a.status === "approved").length;
  const rejectedCount = applications.filter((a) => a.status === "rejected").length;
  const pendingCount = applications.filter((a) => a.status === "pending").length;
  const approvalRate = totalApps > 0 ? Math.round((approvedCount / totalApps) * 100) : 0;
  const rejectionRate = totalApps > 0 ? Math.round((rejectedCount / totalApps) * 100) : 0;
  const pendingRate = totalApps > 0 ? Math.round((pendingCount / totalApps) * 100) : 0;

  const cityDist = useMemo(() => {
    const map: Record<string, number> = {};
    applications.forEach((a) => {
      map[a.city] = (map[a.city] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [applications]);

  const maxCity = cityDist.length > 0 ? cityDist[0][1] : 1;

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Başvuru İstatistikleri
          </h2>
          <p className="text-xs text-stone-400 mt-0.5">Son 6 ay · Toplam {totalApps} başvuru</p>
        </div>
        <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1">
          <button
            onClick={() => setActiveTab("monthly")}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
              ${activeTab === "monthly" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"}`}
          >
            Aylık
          </button>
          <button
            onClick={() => setActiveTab("approval")}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
              ${activeTab === "approval" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"}`}
          >
            Dağılım
          </button>
        </div>
      </div>

      {activeTab === "monthly" ? (
        <div>
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-stone-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-stone-900">{totalApps}</p>
              <p className="text-xs text-stone-400 mt-0.5">Toplam</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
              <p className="text-xs text-stone-400 mt-0.5">Bekleyen</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-emerald-600">{approvedCount}</p>
              <p className="text-xs text-stone-400 mt-0.5">Onaylanan</p>
            </div>
            <div className="bg-rose-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-rose-600">{rejectedCount}</p>
              <p className="text-xs text-stone-400 mt-0.5">Reddedilen</p>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-end gap-2 h-40 px-2">
              {monthlyData.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full flex flex-col justify-end gap-0.5" style={{ height: 120 }}>
                    <div className="w-full flex flex-col-reverse gap-0.5">
                      {m.approved > 0 && (
                        <div
                          className="w-full bg-emerald-400 rounded-sm transition-all duration-500"
                          style={{ height: `${(m.approved / maxTotal) * 100}px` }}
                          title={`Onaylanan: ${m.approved}`}
                        ></div>
                      )}
                      {m.rejected > 0 && (
                        <div
                          className="w-full bg-rose-400 rounded-sm transition-all duration-500"
                          style={{ height: `${(m.rejected / maxTotal) * 100}px` }}
                          title={`Reddedilen: ${m.rejected}`}
                        ></div>
                      )}
                      {m.pending > 0 && (
                        <div
                          className="w-full bg-amber-400 rounded-sm transition-all duration-500"
                          style={{ height: `${(m.pending / maxTotal) * 100}px` }}
                          title={`Bekleyen: ${m.pending}`}
                        ></div>
                      )}
                      {m.total === 0 && (
                        <div className="w-full bg-stone-100 rounded-sm" style={{ height: 4 }}></div>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-stone-400 font-medium">{m.label}</span>
                  {m.total > 0 && (
                    <span className="text-xs font-bold text-stone-700">{m.total}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 justify-center pt-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-amber-400"></div>
                <span className="text-xs text-stone-500">Bekleyen</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-emerald-400"></div>
                <span className="text-xs text-stone-500">Onaylanan</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-rose-400"></div>
                <span className="text-xs text-stone-500">Reddedilen</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="bg-stone-50 rounded-xl p-5">
              <p className="text-xs font-semibold text-stone-600 mb-4">Onay Oranı</p>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20 flex-shrink-0">
                  <div className="w-20 h-20 rounded-full border-8 border-stone-200 flex items-center justify-center">
                    <span className="text-lg font-bold text-emerald-600">{approvalRate}%</span>
                  </div>
                </div>
                <div className="space-y-2 flex-1">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-stone-500">Onaylanan</span>
                      <span className="font-semibold text-emerald-600">{approvedCount}</span>
                    </div>
                    <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-400 rounded-full transition-all duration-700" style={{ width: `${approvalRate}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-stone-500">Reddedilen</span>
                      <span className="font-semibold text-rose-600">{rejectedCount}</span>
                    </div>
                    <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-rose-400 rounded-full transition-all duration-700" style={{ width: `${rejectionRate}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-stone-500">Bekleyen</span>
                      <span className="font-semibold text-amber-600">{pendingCount}</span>
                    </div>
                    <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full transition-all duration-700" style={{ width: `${pendingRate}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-xl p-5">
              <p className="text-xs font-semibold text-stone-600 mb-4">Şehir Dağılımı</p>
              {cityDist.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-4">Henüz başvuru yok</p>
              ) : (
                <div className="space-y-2.5">
                  {cityDist.map(([city, count], i) => (
                    <div key={city}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-stone-600 font-medium">{city}</span>
                        <span className="text-stone-500">{count} başvuru</span>
                      </div>
                      <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${(count / maxCity) * 100}%`,
                            backgroundColor: ["#f59e0b", "#10b981", "#f97316", "#8b5cf6", "#06b6d4"][i % 5],
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-stone-50 rounded-xl p-5">
            <p className="text-xs font-semibold text-stone-600 mb-4">Mutfak Türü Dağılımı</p>
            {(() => {
              const cuisineMap: Record<string, number> = {};
              applications.forEach((a) => { cuisineMap[a.cuisine] = (cuisineMap[a.cuisine] || 0) + 1; });
              const cuisineList = Object.entries(cuisineMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
              const maxC = cuisineList.length > 0 ? cuisineList[0][1] : 1;
              return cuisineList.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-2">Henüz başvuru yok</p>
              ) : (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                  {cuisineList.map(([cuisine, count]) => (
                    <div key={cuisine}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-stone-600 truncate">{cuisine}</span>
                        <span className="text-stone-500 ml-2 flex-shrink-0">{count}</span>
                      </div>
                      <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-400 rounded-full transition-all duration-700"
                          style={{ width: `${(count / maxC) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsChart;
